import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlatformBadge } from "@/components/badges/PlatformBadge";
import { StatusBadge } from "@/components/badges/StatusBadge";
import { useAppState } from "@/state/AppStateProvider";
import { PLATFORM_LABELS } from "@/lib/constants";
import { getAssetReviewWorkflow } from "@/lib/reviewWorkflow";
import { CalendarPlus, Clock, Send, StickyNote, UserRound } from "lucide-react";
import { formatDateTime } from "@/lib/utils";
import { toast } from "sonner";

const PLATFORM_INTEGRATION_IDS = {
  linkedin: "int_linkedin",
  x: "int_x",
  facebook: "int_facebook",
  instagram: "int_instagram",
  youtube_shorts: "int_youtube",
  threads: "int_threads",
  bluesky: "int_bluesky",
};

function nextDefaultDate() {
  const date = new Date(Date.now() + 1000 * 60 * 60 * 24);
  date.setHours(9, 0, 0, 0);
  return date;
}

function formatDateInput(date) {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}

function formatTimeInput(date) {
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

function getInitialDate(asset) {
  if (asset?.schedule_at) {
    const scheduled = new Date(asset.schedule_at);
    if (!Number.isNaN(scheduled.getTime())) return scheduled;
  }
  return nextDefaultDate();
}

export function ScheduleAssetDialog({ asset, trigger, onScheduled }) {
  const { state, scheduleAsset } = useAppState();
  const [open, setOpen] = useState(false);
  const initialDate = useMemo(() => getInitialDate(asset), [asset]);
  const [dateValue, setDateValue] = useState(formatDateInput(initialDate));
  const [timeValue, setTimeValue] = useState(formatTimeInput(initialDate));
  const [accountId, setAccountId] = useState("");
  const [notes, setNotes] = useState(asset?.schedule_notes || "");
  const workflow = useMemo(() => getAssetReviewWorkflow(asset), [asset]);

  const platformName = PLATFORM_LABELS[asset?.platform] || asset?.platform || "Platform";
  const platformIntegration = useMemo(() => {
    const integrationId = PLATFORM_INTEGRATION_IDS[asset?.platform];
    return state.integrations.find((integration) => integration.id === integrationId) || null;
  }, [asset?.platform, state.integrations]);

  const accountOptions = useMemo(() => {
    const connectedOption = platformIntegration?.connected
      ? {
          id: platformIntegration.id,
          label: `${platformName} connected account`,
          hint: "Connected for publishing",
          mode: "connected_account",
        }
      : {
          id: "manual_queue",
          label: `${platformName} manual queue`,
          hint: "Connect the account later",
          mode: "manual_queue",
        };

    return [
      connectedOption,
      {
        id: "manual_review",
        label: "Manual posting reminder",
        hint: "Calendar reminder only",
        mode: "manual_reminder",
      },
    ];
  }, [platformIntegration, platformName]);

  useEffect(() => {
    if (!open) return;
    const next = getInitialDate(asset);
    setDateValue(formatDateInput(next));
    setTimeValue(formatTimeInput(next));
    setAccountId(asset?.scheduled_account_id || accountOptions[0]?.id || "manual_queue");
    setNotes(asset?.schedule_notes || "");
  }, [open, asset, accountOptions]);

  const selectedAccount = accountOptions.find((account) => account.id === accountId) || accountOptions[0];
  const previewDate = dateValue && timeValue ? new Date(`${dateValue}T${timeValue}`) : null;
  const hasValidDate = previewDate && !Number.isNaN(previewDate.getTime());
  const isReschedule = workflow.canReschedule;
  const scheduleBlocked = !workflow.canSchedule;

  const onSubmit = (event) => {
    event.preventDefault();
    if (!asset?.id) return toast.error("No asset selected");
    if (scheduleBlocked) return toast.error("Approve this post before scheduling.");
    if (!hasValidDate) return toast.error("Pick a valid date and time");

    const scheduledAt = previewDate.toISOString();
    scheduleAsset(asset.id, scheduledAt, {
      scheduled_account_id: selectedAccount.id,
      scheduled_account_label: selectedAccount.label,
      schedule_notes: notes.trim(),
      publish_mode: selectedAccount.mode,
    });
    setOpen(false);
    toast.success(`${isReschedule ? "Rescheduled" : "Scheduled"} for ${formatDateTime(scheduledAt)}`);
    onScheduled?.(scheduledAt);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button size="sm" variant="outline" className="gap-1.5">
            <CalendarPlus className="h-3.5 w-3.5" /> {isReschedule ? "Reschedule" : "Schedule"}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-[620px] overflow-hidden border bg-card p-0 text-foreground" data-testid="schedule-asset-dialog" style={{ borderColor: "rgba(18, 22, 20,0.14)" }}>
        <form onSubmit={onSubmit}>
          <DialogHeader className="border-b bg-[color:var(--surface-1)] px-5 py-4 text-left" style={{ borderColor: "rgba(226,232,240,0.9)" }}>
            <div className="flex flex-wrap items-center gap-2">
              <PlatformBadge platform={asset?.platform} />
              {asset?.status && <StatusBadge status={asset.status} />}
            </div>
            <DialogTitle className="mt-3 text-xl tracking-[-0.025em]">
              {isReschedule ? "Reschedule this post" : "Schedule this post"}
            </DialogTitle>
            <DialogDescription>
              {scheduleBlocked
                ? "This post needs approval before it can move onto the calendar."
                : "Pick when this approved post should enter the publishing queue. Nothing auto-publishes without the connected workflow."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 px-5 py-4">
            <div className="rounded-lg border bg-[color:var(--surface-1)] p-3" style={{ borderColor: "rgba(226,232,240,0.9)" }}>
              <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Asset</p>
              <p className="mt-1 line-clamp-2 text-sm font-semibold text-foreground">{asset?.title || "Untitled post"}</p>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="schedule-date" className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                  <CalendarPlus className="h-3.5 w-3.5" /> Date
                </Label>
                <Input
                  id="schedule-date"
                  type="date"
                  value={dateValue}
                  onChange={(event) => setDateValue(event.target.value)}
                  data-testid="schedule-date-input"
                  className="bg-card"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="schedule-time" className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                  <Clock className="h-3.5 w-3.5" /> Time
                </Label>
                <Input
                  id="schedule-time"
                  type="time"
                  value={timeValue}
                  onChange={(event) => setTimeValue(event.target.value)}
                  data-testid="schedule-time-input"
                  className="bg-card"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                <UserRound className="h-3.5 w-3.5" /> Account
              </Label>
              <Select value={accountId} onValueChange={setAccountId}>
                <SelectTrigger className="bg-card" data-testid="schedule-account-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {accountOptions.map((account) => (
                    <SelectItem key={account.id} value={account.id}>
                      {account.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedAccount && (
                <p className="text-[12px] text-muted-foreground">{selectedAccount.hint}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="schedule-notes" className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                <StickyNote className="h-3.5 w-3.5" /> Posting notes
              </Label>
              <Textarea
                id="schedule-notes"
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                rows={3}
                placeholder="Optional reminder for publishing, image treatment, or timing context."
                data-testid="schedule-notes-input"
                className="resize-none bg-card"
              />
            </div>

            <div className="rounded-lg border p-3 text-[12px] leading-relaxed text-muted-foreground" style={{ borderColor: scheduleBlocked ? "rgba(217,119,6,0.22)" : "rgba(18, 147, 79,0.18)", backgroundColor: scheduleBlocked ? "rgba(217,119,6,0.08)" : "rgba(18, 147, 79,0.06)" }}>
              {scheduleBlocked ? (
                "Approve this post first, then scheduling will become available."
              ) : hasValidDate ? (
                <>This will appear on Calendar as <span className="font-semibold text-foreground">{formatDateTime(previewDate)}</span>.</>
              ) : (
                "Pick a date and time to preview the calendar slot."
              )}
            </div>
          </div>

          <DialogFooter className="border-t bg-[color:var(--surface-1)] px-5 py-4" style={{ borderColor: "rgba(226,232,240,0.9)" }}>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={scheduleBlocked} className="gap-1.5 bg-[#0C0F0E] text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-55" data-testid="schedule-confirm-button">
              <Send className="h-3.5 w-3.5" /> {isReschedule ? "Save schedule" : "Schedule post"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
